import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { Alert, InteractionManager, StyleSheet, View } from "react-native"
import { Ionicons } from "@expo/vector-icons"
import api from "../../../api-client"
import { log } from "../../../utility"
import { useAppDispatch, useAppSelector } from '../../../store'
import { fetchAccounts, setAccounts, setInstitutions } from "../../../accounts-slice"
import ThemedDialog from "../../../style/dialog"
import { PlaidLinkConfig, PlaidContextValue, UnlinkState } from "./Plaid.types"
import Loader from "../../loader"
import { fetchCustomerImpact } from "../../../customer-impact-slice"
import { fetchFeaturedTags } from "../../../featured-tags-slice"
import { syncCustomerData } from "../../../auth-slice"
import Haptics from "../../haptics"
import Analytics from "../../analytics"
import { openPlaidLinkSession } from "./PlaidLinkPlatform"
import { goBack as rootGoBack } from "../../../root-navigation"

type PlaidLinkMetadata = Record<string, unknown> & {
  institution?: Record<string, unknown> & { id?: string; name?: string }
  accounts?: unknown[]
}

type AccessTokenInstitution = Parameters<typeof api.getAccessToken>[1]

const PlaidContext = createContext<PlaidContextValue>({
  startPlaidLinkSession: () => Promise.resolve(),
  beginUnlinkPlaidAccount: () => { return },
})

export const usePlaidLink = (): PlaidContextValue => useContext(PlaidContext)

export const PlaidProvider: React.FC<React.PropsWithChildren> = (
  { children }: React.PropsWithChildren
) => {
  const dispatch = useAppDispatch()
  const { institutions, accounts } = useAppSelector((s) => s.accountsSlice)

  const [plaidLoading, setPlaidLoading] = useState(false)
  const [unlinkState, setUnlinkState] = useState<UnlinkState>("idle")
  const [unlinkItem, setUnlinkItem] = useState<
    { id: string; name: string; source?: string; accountsCount?: number } | null
  >(null)
  const [unlinkVisible, setUnlinkVisible] = useState(false)
  const [linkSuccessInfo, setLinkSuccessInfo] = useState<{
    institutionName?: string
    accountsCount?: number
    isUpdate?: boolean
  } | null>(null)
  const [linkSuccessVisible, setLinkSuccessVisible] = useState(false)

  const isMountedRef = useRef(true)
  const linkSuccessTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const linkSuccessDismissTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const unlinkDismissTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const beginUnlinkTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      isMountedRef.current = false
      if (linkSuccessTimeoutRef.current) {
        clearTimeout(linkSuccessTimeoutRef.current)
      }
      if (linkSuccessDismissTimeoutRef.current) {
        clearTimeout(linkSuccessDismissTimeoutRef.current)
      }
      if (unlinkDismissTimeoutRef.current) {
        clearTimeout(unlinkDismissTimeoutRef.current)
      }
      if (beginUnlinkTimeoutRef.current) {
        clearTimeout(beginUnlinkTimeoutRef.current)
      }
    }
  }, [])

  const dismissLinkSuccessDialog = useCallback(() => {
    setLinkSuccessVisible(false)
    // Keep content stable while the modal fade-out animation completes.
    if (linkSuccessDismissTimeoutRef.current) {
      clearTimeout(linkSuccessDismissTimeoutRef.current)
    }
    linkSuccessDismissTimeoutRef.current = setTimeout(() => {
      if (!isMountedRef.current) return
      setLinkSuccessInfo(null)
    }, 250)
  }, [])

  const showLinkSuccessDialog = useCallback((info: {
    institutionName?: string
    accountsCount?: number
    isUpdate?: boolean
  }) => {
    if (linkSuccessDismissTimeoutRef.current) {
      clearTimeout(linkSuccessDismissTimeoutRef.current)
    }
    setLinkSuccessInfo(info)
    setLinkSuccessVisible(true)
  }, [])

  const dismissUnlinkDialog = useCallback(() => {
    setUnlinkVisible(false)
    if (unlinkDismissTimeoutRef.current) {
      clearTimeout(unlinkDismissTimeoutRef.current)
    }
    unlinkDismissTimeoutRef.current = setTimeout(() => {
      if (!isMountedRef.current) return
      setUnlinkState("idle")
      setUnlinkItem(null)
    }, 250)
  }, [])

  const showUnlinkDialog = useCallback((item: {
    id: string
    name: string
    source?: string
    accountsCount?: number
  }) => {
    if (unlinkDismissTimeoutRef.current) {
      clearTimeout(unlinkDismissTimeoutRef.current)
    }
    setUnlinkItem(item)
    setUnlinkState("confirm")
    setUnlinkVisible(true)
  }, [])

  const fetchUpdatedCustomerData = useCallback(async () => {
    dispatch(syncCustomerData())
    dispatch(fetchFeaturedTags())
    dispatch(fetchCustomerImpact())
  }, [dispatch])

  const startPlaidLinkSession = useCallback(
    async ({ item_id, redirectPath }: PlaidLinkConfig = {}) => {
      const source = redirectPath ?? "unknown"
      try {
        setPlaidLoading(true)

        if (item_id) {
          const institution = institutions.find(i => i.item_id === item_id)
          const institutionName = institution?.name
          const institutionRecord = (institution ?? {}) as Record<string, unknown>
          const hadError = !!(institution?.error || !!institutionRecord["hasError"])
          Analytics.track('begin_update_plaid_account', {
            source,
            institutionName,
            hadError,
          })
        } else {
          Analytics.track('begin_link_plaid_account', { source })
        }

        await openPlaidLinkSession({
          item_id,
          redirectPath,
          setIsFetching: setPlaidLoading,
          onSuccess: async ({ publicToken, metadata }) => {
            let successInfo: {
              institutionName?: string
              accountsCount?: number
              isUpdate?: boolean
            } | null = null

            const md = (metadata ?? {}) as PlaidLinkMetadata

            const accountsCount = Array.isArray(md.accounts)
              ? md.accounts.length
              : undefined
            const institutionId = md.institution?.id
            const institutionName = md.institution?.name
            const fallbackInstitutionName = institutions.find(i => i.item_id === item_id)?.name
            const resolvedInstitutionName = institutionName || fallbackInstitutionName

            try {
              // Important: Plaid Link is presented natively. Avoid stacking a second native modal
              // here (can lead to touch-blocking "freezes"). We render our loader as an overlay
              // view (not a `Modal`) so it doesn't compete with Plaid's presentation.
              setPlaidLoading(true)

              if (!md.institution || typeof md.institution.id !== "string") {
                throw new Error("Missing institution in Plaid metadata.")
              }
              const institutionForToken = md.institution as unknown as AccessTokenInstitution

              const { customerLinked } = await api.getAccessToken(
                publicToken,
                institutionForToken
              )

              Analytics.track(item_id ? 'success_update_plaid_account' : 'success_link_plaid_account', {
                source,
                institutionId,
                institutionName: resolvedInstitutionName,
                accountsCount,
              })

              dispatch(fetchAccounts())

              if (customerLinked) {
                fetchUpdatedCustomerData()
              }

              successInfo = {
                institutionName: resolvedInstitutionName,
                accountsCount: accountsCount,
                isUpdate: !!item_id,
              }
            } catch (err) {
              log.error(err, { tags: { message: "error getting access token via Plaid Link" } })
              Analytics.track(item_id ? 'abandon_update_plaid_account' : 'abandon_link_plaid_account', {
                source,
                reason: 'error',
                institutionName: fallbackInstitutionName,
              })
              Alert.alert("Error", "We couldn’t finish connecting your account. Please try again.")
            } finally {
              setPlaidLoading(false)

              if (successInfo) {
                // Show success dialog after interactions settle to avoid the common
                // "touches blocked" race right after dismissing a native modal.
                InteractionManager.runAfterInteractions(() => {
                  if (!isMountedRef.current) return
                  if (linkSuccessTimeoutRef.current) {
                    clearTimeout(linkSuccessTimeoutRef.current)
                  }
                  linkSuccessTimeoutRef.current = setTimeout(() => {
                    if (!isMountedRef.current) return
                    Haptics.notificationSuccess()
                    showLinkSuccessDialog(successInfo)
                  }, 350)
                })
              }
            }
          },
          onExit: (exit) => {
            const exitRecord = (exit ?? {}) as Record<string, unknown>
            const hadError = !!exitRecord["error"]
            const institutionName = institutions.find(i => i.item_id === item_id)?.name
            Analytics.track(item_id ? 'abandon_update_plaid_account' : 'abandon_link_plaid_account', {
              source,
              reason: hadError ? 'error' : 'cancel',
              institutionName,
            })
          },
        })
        return
      } catch (err) {
        setPlaidLoading(false)
        log.error(err, { tags: { message: "error opening Plaid Link" } })
        Alert.alert("Error", "Unable to open Plaid Link.")
        const institutionName = institutions.find(i => i.item_id === item_id)?.name
        Analytics.track(item_id ? 'abandon_update_plaid_account' : 'abandon_link_plaid_account', {
          source,
          reason: 'error',
          institutionName,
        })
      }
    },
    [dispatch, institutions, fetchUpdatedCustomerData, showLinkSuccessDialog]
  )

  const beginUnlinkPlaidAccount = useCallback((item_id: string, name: string, source: string) => {
    Analytics.track('begin_unlink_plaid_account', {
      source,
      institutionName: name,
    })
    const accountsCount = accounts.filter((a) => a.item_id === item_id).length
    if (beginUnlinkTimeoutRef.current) {
      clearTimeout(beginUnlinkTimeoutRef.current)
    }
    beginUnlinkTimeoutRef.current = setTimeout(() => {
      if (!isMountedRef.current) return
      
      showUnlinkDialog({ id: item_id, name, source, accountsCount })
    }, 100)
  }, [accounts, showUnlinkDialog])

  const confirmUnlink = useCallback(async () => {
    if (!unlinkItem) return
    try {
      setUnlinkState("loading")
      const unlinkSource = unlinkItem.source || 'institution-details'
      const { customerUnlinked } = await api.unlinkInstitution(unlinkItem.id)
      // Optimistically update redux store
      const filteredAccounts = accounts.filter((a) => a.item_id !== unlinkItem.id)
      const filteredInstitutions = institutions.filter((i) => i.item_id !== unlinkItem.id)
      
      dispatch(setAccounts(filteredAccounts))
      dispatch(setInstitutions(filteredInstitutions))
      setUnlinkState("success")
      
      Analytics.track('success_unlink_plaid_account', {
        source: unlinkSource,
        institutionName: unlinkItem.name,
      })

      // if the customer was unlinked, sync the customer-related data
      if (customerUnlinked) {
        fetchUpdatedCustomerData()
      }

      // Use root navigation ref so we pop the currently focused nested navigator (Settings stack).
      InteractionManager.runAfterInteractions(() => {
        rootGoBack()
      })
    } catch {
      setUnlinkState("error")
      const unlinkSource = unlinkItem.source || 'institution-details'
      Analytics.track('abandon_unlink_plaid_account', {
        source: unlinkSource,
        institutionName: unlinkItem.name,
        reason: 'error',
      })
    }
  }, [accounts, institutions, unlinkItem, dispatch, fetchUpdatedCustomerData])

  const contextValue = useMemo<PlaidContextValue>(
    () => ({
      startPlaidLinkSession,
      beginUnlinkPlaidAccount,
    }),
    [startPlaidLinkSession, beginUnlinkPlaidAccount]
  )
  const unlinkInstitutionName = unlinkItem?.name || "this institution"
  const unlinkAccountsCount = unlinkItem?.accountsCount || 1

  return (
    <PlaidContext.Provider value={contextValue}>
      {children}

      {/* Full-screen spinner while retrieving link token / exchanging public token
          Note: this is an overlay view (not a `Modal`) to avoid competing with Plaid Link's
          native modal presentation, which can lead to touch-blocking "freezes". */}
      {plaidLoading ? (
        <View
          style={[
            StyleSheet.absoluteFillObject,
            {
              backgroundColor: 'rgba(0,0,0,0.5)',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 9999,
              elevation: 9999,
            }
          ]}
        >
          <Loader size="large" />
        </View>
      ) : null}

      {/* Link success dialog */}
      <ThemedDialog
        title={
          linkSuccessInfo?.isUpdate
            ? "Your account was updated"
            : linkSuccessInfo?.institutionName
              ? `Successfully connected ${linkSuccessInfo.institutionName}`
              : "Successfully connected your account"
        }
        subMessage={
          linkSuccessInfo?.isUpdate
            ? "Your bank connection has been refreshed."
            : "Your account is connected and syncing."
        }
        options={[
          {
            title: "Okay",
            icon: <Ionicons name="checkmark" size={22} />,
            role: "confirm",
            onPress: () => {
              Haptics.impactLight()
              dismissLinkSuccessDialog()
            },
          },
        ]}
        visible={linkSuccessVisible}
        backDropProps={{
          onPress: () => {
            dismissLinkSuccessDialog()
          },
        }}
      />

      {/* Centralized unlink dialog */}
      <ThemedDialog
        title={
          ({
            error: `An error occurred while unlinking your ${unlinkInstitutionName} account${
              unlinkAccountsCount > 1 ? "s" : ""
            }`,
            loading: `Disconnecting ${unlinkInstitutionName}...`,
            confirm: `Disconnect ${unlinkInstitutionName} account${
              unlinkAccountsCount > 1 ? "s" : ""
            }?`,
            success: `Successfully disconnected ${unlinkItem?.name} account${
              unlinkAccountsCount > 1 ? "s" : ""
            }`,
          })[unlinkState] ||
          `Disconnect ${unlinkInstitutionName} account${
            unlinkAccountsCount > 1 ? "s" : ""
          }?`
        }
        subMessage={
          ({
            error: "Please try again later",
            success: `Don't worry. If you change your mind, you can connect ${
              unlinkAccountsCount > 1 ? "these accounts" : "this account"
            } again at any time.`,
            confirm: `Don't worry. If you change your mind, you can connect ${
              unlinkAccountsCount > 1 ? "these accounts" : "this account"
            } again at any time.`,
            loading: `Don't worry. If you change your mind, you can connect ${
              unlinkAccountsCount > 1 ? "these accounts" : "this account"
            } again at any time.`,
          })[unlinkState] ||
          `Don't worry. If you change your mind, you can connect ${
            unlinkAccountsCount > 1 ? "these accounts" : "this account"
          } again at any time.`
        }
        options={
          ({
            error: [
              {
                title: "Okay",
                icon: <Ionicons name="checkmark" size={22} />,
                role: "cancel",
                onPress: () => {
                  Haptics.impactLight()
                  dismissUnlinkDialog()
                },
              },
            ],
            success: [
              {
                title: "Okay",
                icon: <Ionicons name="checkmark" size={22} />,
                role: "confirm",
                onPress: () => {
                  Haptics.impactLight()
                  dismissUnlinkDialog()
                },
              },
            ],
            confirm: [
              {
                title: "Delete",
                icon: <Ionicons name="trash-outline" size={22} />,
                role: "destructive",
                onPress: () => {
                  Haptics.impactLight()
                  confirmUnlink()
                },
              },
              {
                title: "Cancel",
                icon: <Ionicons name="close-outline" size={22} />,
                role: "cancel",
                onPress: () => {
                  Haptics.impactLight()
                  if (unlinkItem) {
                    const unlinkSource = unlinkItem.source || 'institution-details'
                    Analytics.track('abandon_unlink_plaid_account', {
                      source: unlinkSource,
                      institutionName: unlinkItem.name,
                      reason: 'cancel',
                    })
                  }
                  dismissUnlinkDialog()
                }               
              },
            ],
          })[unlinkState] || []
        }
        visible={unlinkVisible}
        errorAnimation={unlinkState === "error"}
        loading={unlinkState === "loading"}
        backDropProps={{
          onPress: () => {
            if (unlinkState === "loading") return
 
            dismissUnlinkDialog()
          },
        }}
      />
    </PlaidContext.Provider>
  )
}

export default PlaidContext


